import { Link } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { SITE } from "../lib/site";
import { Reveal, MaskedLine } from "../components/Reveal";
import ServiceIcon from "../components/ServiceIcon";
import SectionCTA from "../components/SectionCTA";

export default function Services() {
  return (
    <>
      <PageHero
        eyebrow="02 · Services"
        title={
          <>
            Every PennDOT service,
            <br /> <span className="italic text-[#C5A059]">one</span> Philadelphia counter.
          </>
        }
        image={SITE.images.plate}
      />

      <section className="py-20 md:py-28 lg:py-32 bg-[#FBF9F5]" data-testid="services-full">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 md:mb-16">
            <div className="lg:col-span-5">
              <div className="label-mini">Full Menu</div>
              <p className="mt-5 md:mt-6 font-serif text-3xl md:text-4xl leading-tight text-[#0A192F]">
                Walk in. Bring your paperwork. We handle the rest — usually inside thirty minutes.
              </p>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <a href={SITE.phoneHref} className="btn-gold justify-center" data-testid="services-call">
                  <Phone className="w-4 h-4" /> Call to Confirm Docs
                </a>
                <Link to="/contact" className="btn-ghost text-[#0A192F] justify-center" data-testid="services-contact-inline">
                  Ask a Question
                </Link>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {SITE.services.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.03}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group block relative border border-[#0A192F]/10 bg-white hover:bg-[#0A192F] hover:border-[#C5A059]/60 hover:shadow-2xl hover:shadow-[#0A192F]/10 hover:-translate-y-1 transition-all duration-500 ease-out p-7 md:p-9 h-full overflow-hidden"
                  data-testid={`service-item-${s.key}`}
                  data-cursor="hover"
                  aria-label={`${s.name} — ${s.blurb}`}
                >
                  <span className="absolute top-0 right-0 w-12 h-12 border-t border-r border-[#C5A059]/0 group-hover:border-[#C5A059] transition-colors duration-500" />

                  <div className="flex items-center justify-between">
                    <span
                      className="w-16 h-16 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
                      style={{ background: s.color, color: s.color === "#0A192F" ? "#C5A059" : "#0A192F" }}
                    >
                      <ServiceIcon name={s.icon} className="w-7 h-7" />
                    </span>
                    <span className="text-[#C5A059] font-mono-tab text-xs">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="mt-8 font-serif text-2xl text-[#0A192F] group-hover:text-white transition-colors duration-500 leading-tight">
                    {s.name}
                  </h3>
                  <p className="mt-3 text-[#0A192F]/65 group-hover:text-white/75 text-sm leading-relaxed transition-colors duration-500">
                    {s.blurb}
                  </p>

                  <div className="mt-7 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0A192F]/60 group-hover:text-[#C5A059] transition-colors duration-500">
                    Learn More
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-500" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SectionCTA
        eyebrow="Not sure which service?"
        title={<>Tell us what you need. We'll <span className="italic text-[#C5A059]">prep the paperwork.</span></>}
        subtitle="We'll confirm exactly which documents to bring, so your visit takes minutes — not hours."
        secondaryLabel="Get Insurance Quote"
        secondaryTo="/insurance"
        testid="cta-services-final"
      />
    </>
  );
}

export function PageHero({ eyebrow, title, image }) {
  return (
    <section className="relative min-h-[70svh] md:min-h-[75svh] w-full overflow-hidden pt-24 bg-[#0A192F]" data-testid="page-hero">
      <div className="absolute inset-0 z-0">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A192F]/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A192F]/30 to-[#0A192F]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,_rgba(10,25,47,0.75),_transparent_60%)]" />
      </div>
      <div className="relative z-10 min-h-[calc(70svh-6rem)] md:min-h-[calc(75svh-6rem)] max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-10 flex flex-col justify-end pb-14 md:pb-16 text-white">
        <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#C5A059]">
          <span className="w-8 h-px bg-[#C5A059]" />
          {eyebrow}
        </div>
        <h1 className="font-serif mt-5 md:mt-6 text-[10vw] sm:text-[8vw] md:text-[6vw] lg:text-[5.2vw] leading-[0.98] max-w-[16ch] tracking-tight">
          <MaskedLine delay={0.15}>{title}</MaskedLine>
        </h1>
      </div>
    </section>
  );
}
